import React, { useState, useCallback } from "react";
import axios from "axios";
import { DATE_RANGE_OPTIONS, getDateRangePreset } from "./dateRangeUtils";
import {
  Box, Button, Chip, Fade, FormControl, Grid, IconButton,
  MenuItem, Paper, Select, Table, TableBody, TableCell,
  TableContainer, Tooltip, Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import HistoryIcon from "@mui/icons-material/History";
import { T } from "./adminTheme";
import {
  StyledTableRow, FilterField,
  TableStateWrapper, SortableTableHead, useSortableTable, ExpandableRow,
} from "./shared";
import { formatDateTime } from "../../utils/dateFormatter";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const COLUMNS = [
  { id: "id",                label: "Log ID",       minWidth: 70  },
  { id: "cleanup_scope",     label: "Scope",        minWidth: 110 },
  { id: "case_code",         label: "Case",         minWidth: 90  },
  { id: "employee_id",       label: "Employee",     minWidth: 100 },
  { id: "machine_code",      label: "Machine",      minWidth: 100 },
  { id: "job_number",        label: "Job No",       minWidth: 120 },
  { id: "admin_user",        label: "Admin",        minWidth: 100 },
  { id: "reason",            label: "Reason",       minWidth: 160 },
  { id: "created_at",        label: "Timestamp",    minWidth: 160 },
];

export default function SessionCleanupLogsTab({ onSnack }) {
  const theme  = useTheme();
  const t      = T(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  // Tab accent — violet/purple for audit logs
  const ACCENT       = t.violet;
  const ACCENT_ALPHA = t.violetAlpha;
  const ACCENT_TEXT  = isDark ? "#c4b5fd" : "#7c3aed";

  const defaultRange = getDateRangePreset("last_7_days");
  const [datePreset, setDatePreset] = useState("last_7_days");
  const [rawRows,    setRawRows]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  
  const [filters, setFilters] = useState({
    cleanupScope:  "",
    cleanupType:   "",
    employeeId:    "",
    machineCode:   "",
    jobNumber:     "",
    adminUser:     "",
    caseCode:      "",
    startDate:     defaultRange.startDate,
    endDate:       defaultRange.endDate,
  });

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/admin/session-cleanup-logs/search`, {
        cleanupScope: filters.cleanupScope || null,
        cleanupType:  filters.cleanupType  || null,
        employeeId:   filters.employeeId   || null,
        machineCode:  filters.machineCode  || null,
        jobNumber:    filters.jobNumber    || null,
        adminUser:    filters.adminUser    || null,
        caseCode:     filters.caseCode     || null,
        startDate:    filters.startDate    || null,
        endDate:      filters.endDate      || null,
      });
      setRawRows(res.data || []);
    } catch (err) {
      onSnack(err?.response?.data?.message || "Failed to load cleanup logs.", "error");
    } finally {
      setLoading(false);
    }
  }, [filters, onSnack]);

  const { sortedRows: rows, sortField, sortDir, handleSort } =
    useSortableTable(rawRows, "created_at", "desc");

  // Helper to get chip color based on cleanup scope
  const getScopeChipColor = (scope) => {
    if (!scope) return "default";
    const lower = scope.toLowerCase();
    if (lower === "employee") return "primary";
    if (lower === "machine") return "info";
    if (lower === "shift_date") return "warning";
    return "default";
  };

  // Helper to get case code chip color
  const getCaseChipColor = (caseCode) => {
    if (!caseCode) return "default";
    const num = parseInt(caseCode.replace("CASE_", "")) || 0;
    if (num <= 3) return "success";  // CASE_01, CASE_02, CASE_03
    if (num <= 6) return "warning";  // CASE_04, CASE_05, CASE_06
    return "error";  // CASE_07+
  };

  // Parse action_summary JSON safely
  const parseActionSummary = (jsonStr) => {
    try {
      return typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
    } catch {
      return {};
    }
  };

  return (
    <Fade in>
      <Box>
        <Paper sx={{
          borderRadius: "14px",
          border: `0.5px solid ${t.border}`,
          background: t.s0,
          boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.25)" : "none",
          overflow: "hidden",
        }}>

          {/* ── Card header ─────────────────────────────────────────── */}
          <Box sx={{
            px: 3, py: 2,
            borderBottom: `0.5px solid ${t.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: isDark
              ? `linear-gradient(135deg, ${ACCENT_ALPHA} 0%, transparent 60%)`
              : "transparent",
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
                background: ACCENT_ALPHA,
                border: `0.5px solid ${ACCENT}30`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: ACCENT,
                boxShadow: isDark ? `0 0 12px ${ACCENT}15` : "none",
              }}>
                <HistoryIcon sx={{ fontSize: 19 }} />
              </Box>

              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 400, color: t.heading, letterSpacing: "-0.01em", lineHeight: 1.2 }}>
                Full audit trail of session recovery operations
                </Typography>
      
              </Box>

              {rows.length > 0 && (
                <Box sx={{ px: 1.25, py: 0.25, borderRadius: "20px", background: isDark ? t.s3 : t.s2, border: `0.5px solid ${t.border}` }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: t.secondary }}>{rows.length}</Typography>
                </Box>
              )}
            </Box>

            <IconButton size="small" onClick={search}
              sx={{ border: `0.5px solid ${t.border}`, borderRadius: "8px", width: 32, height: 32, color: t.muted,
                "&:hover": { background: t.s2, borderColor: t.borderHov, color: t.secondary } }}>
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* ── Filter section ──────────────────────────────────────── */}
          <Box sx={{ px: 3, pt: 2.5, pb: 2.5 }}>
            <Box sx={{ p: 2, borderRadius: "12px", border: `0.5px solid ${t.border}`, background: t.s1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4} lg={2.4} xl={1.7}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={filters.cleanupScope}
                      onChange={(e) => setFilters((f) => ({ ...f, cleanupScope: e.target.value }))}
                      displayEmpty
                    >
                      <MenuItem value="">All Scopes</MenuItem>
                      <MenuItem value="employee">Employee</MenuItem>
                      <MenuItem value="machine">Machine</MenuItem>
                      <MenuItem value="shift_date">Shift Date</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2.4} xl={1.7}>
                  <FilterField label="Case Code" value={filters.caseCode}
                    placeholder="CASE_01, CASE_02..."
                    onChange={(e) => setFilters((f) => ({ ...f, caseCode: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2.4} xl={1.7}>
                  <FilterField label="Employee ID" value={filters.employeeId}
                    onChange={(e) => setFilters((f) => ({ ...f, employeeId: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2.4} xl={1.7}>
                  <FilterField label="Machine Code" value={filters.machineCode}
                    onChange={(e) => setFilters((f) => ({ ...f, machineCode: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2.4} xl={1.7}>
                  <FilterField label="Job Number" value={filters.jobNumber}
                    onChange={(e) => setFilters((f) => ({ ...f, jobNumber: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2.4} xl={1.7}>
                  <FilterField label="Admin User" value={filters.adminUser}
                    onChange={(e) => setFilters((f) => ({ ...f, adminUser: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2}>
                  <FormControl fullWidth size="small">
                    <Select value={datePreset} onChange={(e) => {
                      const val = e.target.value;
                      setDatePreset(val);
                      if (val !== "custom") {
                        const range = getDateRangePreset(val);
                        setFilters((f) => ({ ...f, startDate: range.startDate, endDate: range.endDate }));
                      }
                    }}>
                      {DATE_RANGE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={2.4} xl={2}>
                  <FilterField label="Start Date" type="datetime-local"
                    value={filters.startDate ? filters.startDate.replace(" ", "T") : ""}
                    onChange={(e) => { setDatePreset("custom"); setFilters((f) => ({ ...f, startDate: e.target.value ? e.target.value.replace("T", " ") : "" })); }} />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={2.4} xl={2}>
                  <FilterField label="End Date" type="datetime-local"
                    value={filters.endDate ? filters.endDate.replace(" ", "T") : ""}
                    onChange={(e) => { setDatePreset("custom"); setFilters((f) => ({ ...f, endDate: e.target.value ? e.target.value.replace("T", " ") : "" })); }} />
                </Grid>
              </Grid>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, pt: 2, borderTop: `0.5px solid ${t.border}` }}>
                {rows.length > 0 ? (
                  <Chip size="small"
                    label={`${rows.length} cleanup log${rows.length !== 1 ? "s" : ""} found`}
                    sx={{ background: ACCENT_ALPHA, color: ACCENT_TEXT, border: `0.5px solid ${ACCENT}30`, fontWeight: 600, fontSize: 11 }}
                  />
                ) : <Box />}

                <Button variant="contained" onClick={search} disabled={loading}
                  startIcon={loading ? null : <SearchIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    fontWeight: 600, fontSize: 13, borderRadius: "8px",
                    background: isDark
                      ? `linear-gradient(135deg, #8b5cf6, #7c3aed)`
                      : `linear-gradient(135deg, #8b5cf6, #7c3aed)`,
                    color: "#fff",
                    boxShadow: isDark ? `0 2px 8px ${ACCENT}30` : `0 2px 6px ${ACCENT}40`,
                    "&:hover": { boxShadow: isDark ? `0 4px 14px ${ACCENT}40` : `0 4px 12px ${ACCENT}50`, transform: "translateY(-0.5px)" },
                    "&:disabled": { opacity: 0.6 },
                  }}>
                  {loading ? "Searching…" : "Search Logs"}
                </Button>
              </Box>
            </Box>
          </Box>

          {/* ── Table ───────────────────────────────────────────────── */}
          <TableStateWrapper
            loading={loading}
            empty={rows.length === 0}
            emptyMessage="No session cleanup logs found. Try adjusting your date range or filters."
            colSpan={COLUMNS.length} minHeight={320} skeletonRows={6}
          >
            <TableContainer>
              <Table size="small">
                <SortableTableHead columns={COLUMNS} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <TableBody>
                  {rows.map((row, idx) => {
                    const summary = parseActionSummary(row.action_summary);
                    const isExpanded = expandedRow === row.id;
                    
                    return (
                      <React.Fragment key={`${row.id}-${idx}`}>
                        <StyledTableRow hover onClick={() => setExpandedRow(isExpanded ? null : row.id)}>
                          <TableCell sx={{ fontFamily: "monospace", fontWeight: 600, color: t.muted }}>
                            #{row.id}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              label={row.cleanup_scope || "—"}
                              color={getScopeChipColor(row.cleanup_scope)}
                              sx={{ fontSize: 10, fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              label={row.case_code || "—"}
                              color={getCaseChipColor(row.case_code)}
                              sx={{ fontSize: 10, fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontFamily: "monospace", fontWeight: 700, color: t.heading }}>
                            {row.employee_id || "—"}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {row.machine_code || "—"}
                          </TableCell>
                          <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                            {row.job_number || "—"}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: t.blue }}>
                            {row.admin_user || "—"}
                          </TableCell>
                          <TableCell sx={{ color: t.secondary, fontSize: 12 }}>
                            <Tooltip title={row.reason || ""} placement="top">
                              <Box sx={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {row.reason || "—"}
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {formatDateTime(row.created_at)}
                          </TableCell>
                        </StyledTableRow>
                        
                        {/* Expandable detail row */}
                        {isExpanded && (
                          <ExpandableRow colSpan={COLUMNS.length}>
                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2 }}>
                              {summary.releasedEmployee !== undefined && (
                                <Box>
                                  <Typography sx={{ fontSize: 11, color: t.muted, fontWeight: 600, mb: 0.5 }}>Released Employee</Typography>
                                  <Typography sx={{ fontSize: 12, color: summary.releasedEmployee ? t.green : t.muted }}>
                                    {summary.releasedEmployee ? "Yes" : "No"}
                                  </Typography>
                                </Box>
                              )}
                              {summary.releasedEmployees !== undefined && (
                                <Box>
                                  <Typography sx={{ fontSize: 11, color: t.muted, fontWeight: 600, mb: 0.5 }}>Released Employees</Typography>
                                  <Typography sx={{ fontSize: 12, color: t.heading }}>
                                    {summary.releasedEmployees}
                                  </Typography>
                                </Box>
                              )}
                              {summary.releasedMachine !== undefined && (
                                <Box>
                                  <Typography sx={{ fontSize: 11, color: t.muted, fontWeight: 600, mb: 0.5 }}>Released Machine</Typography>
                                  <Typography sx={{ fontSize: 12, color: summary.releasedMachine ? t.green : t.muted }}>
                                    {summary.releasedMachine ? "Yes" : "No"}
                                  </Typography>
                                </Box>
                              )}
                              {summary.releasedUser !== undefined && (
                                <Box>
                                  <Typography sx={{ fontSize: 11, color: t.muted, fontWeight: 600, mb: 0.5 }}>Released User</Typography>
                                  <Typography sx={{ fontSize: 12, color: summary.releasedUser ? t.green : t.muted }}>
                                    {summary.releasedUser ? "Yes" : "No"}
                                  </Typography>
                                </Box>
                              )}
                              {summary.closedShift !== undefined && (
                                <Box>
                                  <Typography sx={{ fontSize: 11, color: t.muted, fontWeight: 600, mb: 0.5 }}>Closed Shift</Typography>
                                  <Typography sx={{ fontSize: 12, color: summary.closedShift ? t.green : t.muted }}>
                                    {summary.closedShift ? "Yes" : "No"}
                                  </Typography>
                                </Box>
                              )}
                              {summary.closedJobsummary !== undefined && (
                                <Box>
                                  <Typography sx={{ fontSize: 11, color: t.muted, fontWeight: 600, mb: 0.5 }}>Closed Job Summary</Typography>
                                  <Typography sx={{ fontSize: 12, color: summary.closedJobsummary ? t.green : t.muted }}>
                                    {summary.closedJobsummary ? "Yes" : "No"}
                                  </Typography>
                                </Box>
                              )}
                              {summary.closedJobOperations !== undefined && (
                                <Box>
                                  <Typography sx={{ fontSize: 11, color: t.muted, fontWeight: 600, mb: 0.5 }}>Closed Job Operations</Typography>
                                  <Typography sx={{ fontSize: 12, color: t.heading }}>
                                    {summary.closedJobOperations}
                                  </Typography>
                                </Box>
                              )}
                              {summary.closedNonJobOperations !== undefined && (
                                <Box>
                                  <Typography sx={{ fontSize: 11, color: t.muted, fontWeight: 600, mb: 0.5 }}>Closed Non-Job Operations</Typography>
                                  <Typography sx={{ fontSize: 12, color: t.heading }}>
                                    {summary.closedNonJobOperations}
                                  </Typography>
                                </Box>
                              )}
                              {summary.cleanupTime && (
                                <Box>
                                  <Typography sx={{ fontSize: 11, color: t.muted, fontWeight: 600, mb: 0.5 }}>Cleanup Time</Typography>
                                  <Typography sx={{ fontSize: 12, color: t.secondary }}>
                                    {formatDateTime(summary.cleanupTime)}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </ExpandableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </TableStateWrapper>
        </Paper>
      </Box>
    </Fade>
  );
}